/**
 * Report Dashboard Component
 * Shows comprehensive session performance report.
 * Handles both standard (text/AI) sessions and voice interview sessions.
 */
import React from 'react';

const ScoreBar = ({ score }) => (
  <div className="metric-bar">
    <div className="metric-fill">
      <div className="metric-progress" style={{ width: `${Math.max(0, Math.min(100, score))}%` }} />
    </div>
    <span className="metric-percentage">{Math.round(score)}%</span>
  </div>
);

const scoreLabel = (score) => {
  if (score >= 80) return { text: 'EXCELLENT', cls: 'badge-strong' };
  if (score >= 60) return { text: 'GOOD', cls: 'badge-moderate' };
  if (score >= 40) return { text: 'ADEQUATE', cls: 'badge-weak' };
  return { text: 'NEEDS WORK', cls: 'badge-poor' };
};

// ── Voice interview: split-screen per-question cards ─────────────────────────
const VoiceResults = ({ evaluations }) => (
  <div className="detailed-results">
    <h2 className="results-section-title">🎙️ Question-by-Question Review</h2>
    {evaluations.map((ev, i) => {
      const label = scoreLabel(ev.score);
      return (
        <div key={i} className="result-item" style={{ marginBottom: 'var(--space-6)' }}>

          {/* Question header */}
          <div className="result-header">
            <span className="result-question">Q{ev.question_index ?? i + 1}: {ev.question}</span>
            <span className={`result-badge ${label.cls}`}>{label.text} {Math.round(ev.score)}%</span>
          </div>

          {/* Split screen: Your Answer | Reference Solution */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 'var(--space-4)',
            margin: 'var(--space-4) 0',
          }}>
            {/* Left — candidate answer */}
            <div style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-4)',
            }}>
              <div style={{
                fontSize: 'var(--text-xs)', fontWeight: 600,
                color: 'var(--primary)', marginBottom: 'var(--space-3)',
                textTransform: 'uppercase', letterSpacing: '0.05em',
              }}>
                💬 Your Answer
              </div>
              <p style={{
                fontStyle: 'italic', color: 'var(--text-secondary)',
                fontSize: 'var(--text-sm)', lineHeight: 1.6, margin: 0,
              }}>
                {ev.answer ? `"${ev.answer}"` : 'No answer recorded'}
              </p>
            </div>

            {/* Right — reference solution */}
            <div style={{
              background: 'rgba(34,197,94,0.04)',
              border: '1px solid rgba(34,197,94,0.18)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-4)',
            }}>
              <div style={{
                fontSize: 'var(--text-xs)', fontWeight: 600,
                color: '#22c55e', marginBottom: 'var(--space-3)',
                textTransform: 'uppercase', letterSpacing: '0.05em',
              }}>
                ✓ Reference Solution
              </div>
              <p style={{
                color: 'var(--text-primary)',
                fontSize: 'var(--text-sm)', lineHeight: 1.6, margin: 0,
              }}>
                {ev.reference_answer || 'Solution not available'}
              </p>
            </div>
          </div>

          {/* Score + Feedback + Strengths / Improvements */}
          <div className="result-details">
            <div className="result-row">
              <div className="result-label">Score</div>
              <div className="result-value"><ScoreBar score={ev.score} /></div>
            </div>

            <div className="result-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
              <div className="result-label">Feedback</div>
              <div className="result-value" style={{ color: 'var(--text-primary)', fontSize: 'var(--text-sm)' }}>
                {ev.feedback}
              </div>
            </div>

            {ev.strengths && ev.strengths.length > 0 && (
              <div className="result-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                <div className="result-label" style={{ color: 'var(--success)' }}>✓ Strengths</div>
                <ul style={{ margin: 0, paddingLeft: '16px', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                  {ev.strengths.map((s, j) => <li key={j}>{s}</li>)}
                </ul>
              </div>
            )}

            {ev.improvements && ev.improvements.length > 0 && (
              <div className="result-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                <div className="result-label" style={{ color: 'var(--warning, #f59e0b)' }}>↑ Improvements</div>
                <ul style={{ margin: 0, paddingLeft: '16px', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                  {ev.improvements.map((s, j) => <li key={j}>{s}</li>)}
                </ul>
              </div>
            )}
          </div>
        </div>
      );
    })}
  </div>
);

// ── Standard session: concept performance cards ───────────────────────────────
const ConceptResults = ({ conceptPerformance }) => {
  const emoji = (status) => ({ strong: '🌟', moderate: '👍', weak: '⚠️' }[status] || '');
  return (
    <div className="detailed-results">
      <h2 className="results-section-title">📚 Concept Performance</h2>
      <div className="results-grid">
        {conceptPerformance.map((concept) => (
          <div key={concept.concept} className="result-item">
            <div className="result-header">
              <span className="result-question">{emoji(concept.status)} {concept.concept}</span>
              <span className={`result-badge badge-${concept.status}`}>{concept.status.toUpperCase()}</span>
            </div>
            <div className="result-details">
              <div className="result-row">
                <div className="result-label">Average Score</div>
                <div className="result-value"><ScoreBar score={concept.average_score} /></div>
              </div>
              <div className="result-row">
                <div className="result-label">Questions</div>
                <div className="result-value">{concept.total_questions} attempts</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────
const ReportDashboard = ({ report, onStartNewSession, onExit }) => {
  const isVoice = !!report.voice_interview;
  const metrics = report.metrics || {};
  const conceptPerformance = report.concept_performance || [];
  const questionEvaluations = report.question_evaluations || [];

  const totalQ = metrics.total_questions ?? 0;
  const attempted = metrics.attempted_questions ?? totalQ;
  const correct = metrics.correct_answers ?? 0;
  const avgScore = metrics.average_score ?? 0;
  const totalTime = metrics.total_time ?? 0;
  const accuracyPct = attempted > 0 ? (correct / attempted) * 100 : 0;

  return (
    <div className="report-container">
      <div className="report-wrapper">
        <div className="report-header">
          <h1 className="report-title">{isVoice ? '🎙️ Interview Complete' : '📊 Session Complete'}</h1>
          <p className="report-subtitle">
            {isVoice
              ? 'Your voice interview has been evaluated by AI'
              : 'Review your performance and areas for improvement'}
          </p>
        </div>

        {/* Overall assessment (voice only) */}
        {isVoice && report.overall_assessment && (
          <div style={{
            padding: 'var(--space-5)',
            background: 'rgba(99,102,241,0.06)',
            border: '1px solid rgba(99,102,241,0.2)',
            borderRadius: 'var(--radius-lg)',
            marginBottom: 'var(--space-6)',
            fontSize: 'var(--text-base)',
            lineHeight: 1.6,
            color: 'var(--text-primary)',
          }}>
            <div style={{ fontWeight: 600, marginBottom: 'var(--space-2)', color: 'var(--primary)' }}>
              Overall Assessment
            </div>
            {report.overall_assessment}
          </div>
        )}

        {/* Summary Cards */}
        <div className="report-summary">
          <div className="summary-card">
            <div className="summary-icon">📈</div>
            <div className="summary-value">{avgScore.toFixed(1)}</div>
            <div className="summary-label">Average Score (/100)</div>
          </div>

          <div className="summary-card">
            <div className="summary-icon">✅</div>
            <div className="summary-value">{accuracyPct.toFixed(0)}%</div>
            <div className="summary-label">{isVoice ? 'Questions Passed' : 'Accuracy Rate'}</div>
          </div>

          <div className="summary-card">
            <div className="summary-icon">⏱️</div>
            <div className="summary-value">{(totalTime / 60).toFixed(1)}m</div>
            <div className="summary-label">Total Time</div>
          </div>

          <div className="summary-card">
            <div className="summary-icon">❓</div>
            <div className="summary-value">{totalQ}</div>
            <div className="summary-label">Questions</div>
          </div>
        </div>

        {/* Voice: per-question evaluation */}
        {isVoice && questionEvaluations.length > 0 && (
          <VoiceResults evaluations={questionEvaluations} />
        )}

        {/* Standard: concept performance */}
        {!isVoice && conceptPerformance.length > 0 && (
          <ConceptResults conceptPerformance={conceptPerformance} />
        )}

        {/* Voice: concept performance (if returned) */}
        {isVoice && conceptPerformance.length > 0 && (
          <ConceptResults conceptPerformance={conceptPerformance} />
        )}

        {/* Recommendations */}
        {report.recommendations && report.recommendations.length > 0 && (
          <div className="tips-section">
            <h3 className="tips-title">🎯 Recommendations for Improvement</h3>
            <ul className="tips-list">
              {report.recommendations.map((rec, i) => (
                <li key={i} className="tip-item">{rec}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Session Details */}
        <div className="chart-container">
          <h3 className="chart-title">📋 Session Details</h3>
          <div className="stat-row">
            <span className="stat-name">Topics Covered</span>
            <span className="stat-value">{(report.topics || []).join(', ')}</span>
          </div>
          <div className="stat-row">
            <span className="stat-name">Session Duration</span>
            <span className="stat-value">
              {Math.floor(totalTime / 60)} min {Math.floor(totalTime % 60)}s
            </span>
          </div>
          {metrics.average_time_per_question != null && (
            <div className="stat-row">
              <span className="stat-name">Avg Time per Question</span>
              <span className="stat-value">{metrics.average_time_per_question.toFixed(1)}s</span>
            </div>
          )}
          <div className="stat-row">
            <span className="stat-name">Session ID</span>
            <span className="stat-value" style={{ fontSize: 'var(--text-xs)', fontFamily: 'var(--font-family-mono)' }}>
              {report.session_id}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="report-actions">
          <button onClick={onStartNewSession} className="retake-btn">
            🔄 Start New Session
          </button>
          <button onClick={onExit} className="new-btn">
            ← Back to Menu
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportDashboard;
